import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

export default function SideCart() {
  const cart = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const goToCheckout = async () => {
    if (cart.list.length === 0) {
      toast.error("Your pack is empty. Add gear first.");
      return;
    }
    if (!cart.termsAccepted) {
      toast.error("Please accept the rental agreement to continue.");
      return;
    }
    setBusy(true);
    try {
      const items = cart.list.map((l) => ({
        item_id: l.item.id,
        quantity: l.quantity,
        extras: l.extras || [],
        note: l.note || "",
      }));
      const { data: booking } = await api.post("/bookings", {
        items,
        rental_days: cart.rentalDays,
        start_date: new Date(cart.startDate).toISOString(),
        customer_phone: cart.customerPhone || null,
        late_checkout: cart.lateCheckout,
        terms_accepted: cart.termsAccepted,
        pod_slug: cart.podSlug || "park-hill",
      });
      const { data: session } = await api.post("/checkout/session", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      cart.clear();
      window.location.href = session.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "INSUFFICIENT_STOCK") {
        const lines = (detail.conflicts || [])
          .map(
            (c) =>
              `${c.name}: you asked for ${c.requested}, only ${c.available} available`
          )
          .join(" • ");
        toast.error(`Stock conflict — ${lines}`, { duration: 7000 });
      } else {
        toast.error(formatApiErrorDetail(detail) || "Checkout failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const startDateObj = new Date(cart.startDate + "T00:00:00");

  return (
    <Sheet open={cart.open} onOpenChange={cart.setOpen}>
      <SheetContent
        side="right"
        className="invoice-paper p-0 w-full sm:max-w-md flex flex-col"
        data-testid="side-cart"
      >
        <SheetHeader className="bg-brand-blue text-white px-6 py-5 border-b-4 border-brand-gold flex-shrink-0">
          <SheetTitle className="text-white font-black uppercase tracking-tightest text-2xl flex items-center gap-3">
            <i className="fas fa-receipt text-brand-gold" />
            Your Pack
          </SheetTitle>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/70">
            Park Hill Pod • Invoice Preview
          </p>
        </SheetHeader>

        {/* Rental window */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white/70 flex-shrink-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
            Rental Window
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="text-left rounded-xl border border-slate-200 px-3 py-2 hover:border-brand-blue transition-colors"
                  data-testid="cart-start-date-button"
                >
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Pick Up
                  </p>
                  <p className="text-sm font-black text-brand-blue">
                    {startDateObj.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={startDateObj}
                  onSelect={(d) => {
                    if (d) cart.setStartDate(d.toISOString().slice(0, 10));
                  }}
                  disabled={(d) => d < new Date(new Date().toDateString())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <div className="rounded-xl border border-slate-200 px-3 py-2 flex flex-col">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                Days
              </p>
              <div className="flex items-center justify-between mt-1">
                <button
                  onClick={() => cart.setRentalDays(Math.max(1, cart.rentalDays - 1))}
                  className="w-7 h-7 rounded-md bg-slate-100 text-brand-blue active:scale-95"
                  data-testid="cart-days-decrement"
                >
                  <i className="fas fa-minus text-[10px]" />
                </button>
                <span
                  className="font-black text-brand-blue text-base"
                  data-testid="cart-rental-days"
                >
                  {cart.rentalDays}
                </span>
                <button
                  onClick={() => cart.setRentalDays(Math.min(30, cart.rentalDays + 1))}
                  className="w-7 h-7 rounded-md bg-brand-blue text-white active:scale-95"
                  data-testid="cart-days-increment"
                >
                  <i className="fas fa-plus text-[10px]" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lines */}
        <div className="flex-1 overflow-y-auto cart-scroll px-6 py-4 space-y-3 min-h-0">
          {cart.list.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <i className="fas fa-shopping-basket text-5xl mb-4 opacity-30" />
              <p className="text-xs font-black uppercase tracking-widest">No gear yet</p>
              <p className="text-xs mt-2 text-slate-400 font-medium">
                Add tents, tables, and chairs from the inventory.
              </p>
            </div>
          ) : (
            <>
              {cart.list.map((line) => <CartLine key={line.item.id} line={line} />)}

              {/* Sunday upsell */}
              {cart.endsOnSunday && (
                <label
                  className={`block rounded-2xl border-2 p-4 cursor-pointer transition-colors ${
                    cart.lateCheckout
                      ? "border-brand-gold bg-brand-gold/10"
                      : "border-dashed border-brand-gold/60 bg-amber-50/50 hover:bg-amber-50"
                  }`}
                  data-testid="cart-late-checkout-toggle"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={cart.lateCheckout}
                      onChange={(e) => cart.setLateCheckout(e.target.checked)}
                      className="mt-1 accent-brand-blue w-4 h-4"
                      data-testid="cart-late-checkout-checkbox"
                    />
                    <div className="flex-1">
                      <p className="text-xs font-black uppercase text-brand-blue tracking-tightest flex items-center gap-2">
                        <i className="fas fa-sun text-brand-gold" />
                        Sunday Late Checkout
                      </p>
                      <p className="text-[11px] text-slate-600 font-medium mt-1 leading-snug">
                        Add 3 hours of grace time to your return window. Pod stays unlocked
                        until early afternoon. Most weekend events grab this.
                      </p>
                      <p className="text-[10px] font-black uppercase tracking-widest text-brand-red mt-1.5">
                        +$15 flat
                      </p>
                    </div>
                  </div>
                </label>
              )}

              {/* Phone for SMS */}
              <div className="rounded-2xl border border-slate-200 p-3">
                <label
                  htmlFor="cart-phone"
                  className="text-[10px] font-black uppercase tracking-widest text-brand-blue flex items-center gap-2"
                >
                  <i className="fas fa-mobile-alt" />
                  Mobile (optional — for PIN text)
                </label>
                <input
                  id="cart-phone"
                  type="tel"
                  value={cart.customerPhone}
                  onChange={(e) => cart.setCustomerPhone(e.target.value)}
                  placeholder="+1 720 555 1212"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
                  data-testid="cart-phone-input"
                />
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1.5">
                  We'll text your 6-digit PIN here. STOP to opt out.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Totals + checkout */}
        <div className="border-t-2 border-dashed border-slate-300 px-6 py-5 bg-white flex-shrink-0">
          <div className="space-y-1.5 text-sm">
            <Row
              label="Subtotal"
              value={`$${cart.totals.subtotal.toFixed(2)}`}
              testId="cart-subtotal"
            />
            {cart.totals.extras > 0 && (
              <Row
                label="Add-ons & service"
                value={`$${cart.totals.extras.toFixed(2)}`}
                testId="cart-extras"
              />
            )}
            {cart.totals.lateCheckout > 0 && (
              <Row
                label="Sunday late checkout (+3 hrs)"
                value={`$${cart.totals.lateCheckout.toFixed(2)}`}
                testId="cart-late-checkout-line"
              />
            )}
            <Row
              label="Denver tax (8.15%)"
              value={`$${cart.totals.tax.toFixed(2)}`}
              testId="cart-tax"
            />
            <div className="flex justify-between font-black uppercase text-brand-blue text-lg pt-2 border-t border-slate-100 mt-2">
              <span>Charged Today</span>
              <span data-testid="cart-total">${cart.totals.total.toFixed(2)}</span>
            </div>
            <div
              className="mt-3 rounded-xl bg-brand-blue/5 border border-brand-blue/10 px-3 py-2 flex items-center justify-between"
              data-testid="cart-deposit-hold"
            >
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue flex items-center gap-1.5">
                  <i className="fas fa-shield-halved" /> Auth-Hold
                </p>
                <p className="text-[10px] font-medium text-slate-500 mt-0.5">
                  Refundable — released after Pod is relocked.
                </p>
              </div>
              <span className="text-base font-black text-brand-blue" data-testid="cart-deposit">
                ${cart.totals.deposit.toFixed(2)}
              </span>
            </div>
          </div>

          <label
            className={`mt-4 flex items-start gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${
              cart.termsAccepted
                ? "border-brand-blue bg-brand-blue/5"
                : "border-slate-200 hover:border-brand-blue/40"
            }`}
            data-testid="cart-terms-toggle"
          >
            <input
              type="checkbox"
              checked={cart.termsAccepted}
              onChange={(e) => cart.setTermsAccepted(e.target.checked)}
              className="mt-0.5 accent-brand-blue w-4 h-4 flex-shrink-0"
              data-testid="cart-terms-checkbox"
            />
            <span className="text-[10px] font-medium text-slate-600 leading-snug">
              I accept the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                className="font-black text-brand-blue underline"
              >
                rental agreement
              </a>
              , agree to a $50 refundable security hold, and accept liability for damaged or
              missing gear. Renter must be 18+.
            </span>
          </label>

          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-3 text-center">
            <i className="fas fa-id-card mr-1.5 text-brand-blue" />
            One-time photo ID upload after checkout
          </p>

          <button
            onClick={goToCheckout}
            disabled={busy || cart.list.length === 0}
            className="btn-primary mt-4 w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cart-checkout-button"
          >
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Securing Pin...
              </>
            ) : (
              <>
                <i className="fas fa-lock" /> Pay & Get Pin
              </>
            )}
          </button>
          {!user && cart.list.length > 0 && (
            <p className="text-center text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-3">
              Tip:{" "}
              <button
                onClick={() => {
                  cart.setOpen(false);
                  navigate("/register");
                }}
                className="underline text-brand-red"
                data-testid="cart-create-account-link"
              >
                Create an account
              </button>{" "}
              to add gear mid-event
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CartLine({ line }) {
  const cart = useCart();
  const cost = cart.lineCost(line);
  const extrasIndex = Object.fromEntries(
    (line.item.extras || []).map((e) => [e.id, e])
  );
  const [editingNote, setEditingNote] = useState(false);
  const [draftNote, setDraftNote] = useState(line.note || "");

  const saveNote = () => {
    cart.setNote(line.item.id, draftNote.trim().slice(0, 240));
    setEditingNote(false);
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-3"
      data-testid={`cart-line-${line.item.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center flex-shrink-0">
          <i className={`fas ${line.item.icon} text-lg`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-xs uppercase text-brand-blue truncate">
            {line.item.name}
          </p>
          <p className="text-[10px] font-bold text-slate-500">
            ${line.item.price_per_day.toFixed(2)}/day × {cart.rentalDays}d
          </p>
        </div>
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => cart.setQty(line.item.id, line.quantity - 1)}
            className="w-6 h-6 rounded text-brand-blue active:scale-95"
            data-testid={`cart-line-${line.item.id}-decrement`}
          >
            <i className="fas fa-minus text-[9px]" />
          </button>
          <span
            className="w-7 text-center font-black text-brand-blue text-xs"
            data-testid={`cart-line-${line.item.id}-qty`}
          >
            {line.quantity}
          </span>
          <button
            onClick={() => cart.setQty(line.item.id, line.quantity + 1)}
            className="w-6 h-6 rounded bg-brand-blue text-white active:scale-95"
            data-testid={`cart-line-${line.item.id}-increment`}
          >
            <i className="fas fa-plus text-[9px]" />
          </button>
        </div>
        <p className="text-sm font-black text-brand-red w-16 text-right">
          ${cost.total.toFixed(2)}
        </p>
      </div>

      {line.extras && line.extras.length > 0 && (
        <ul
          className="mt-2 ml-6 border-l-2 border-brand-gold/40 space-y-1"
          data-testid={`cart-line-${line.item.id}-extras`}
        >
          {line.extras.map((eid) => {
            const e = extrasIndex[eid];
            if (!e) return null;
            return (
              <li key={eid} className="flex items-center justify-between pl-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  + {e.label}
                </span>
                <button
                  onClick={() =>
                    cart.setExtras(
                      line.item.id,
                      (line.extras || []).filter((x) => x !== eid)
                    )
                  }
                  className="text-[10px] font-black text-slate-300 hover:text-brand-red transition-colors"
                  data-testid={`cart-line-${line.item.id}-remove-extra-${eid}`}
                >
                  <i className="fas fa-times" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Note */}
      <div className="mt-2 ml-6" data-testid={`cart-line-${line.item.id}-note-block`}>
        {editingNote ? (
          <div className="space-y-1.5">
            <textarea
              autoFocus
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value.slice(0, 240))}
              rows={2}
              placeholder="Note for the Pod team"
              className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 resize-none"
              data-testid={`cart-line-${line.item.id}-note-input`}
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setDraftNote(line.note || "");
                  setEditingNote(false);
                }}
                className="text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-brand-red"
                data-testid={`cart-line-${line.item.id}-note-cancel`}
              >
                Cancel
              </button>
              <button
                onClick={saveNote}
                className="bg-brand-blue text-white px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest hover:bg-brand-blue/90"
                data-testid={`cart-line-${line.item.id}-note-save`}
              >
                Save
              </button>
            </div>
          </div>
        ) : line.note ? (
          <button
            onClick={() => {
              setDraftNote(line.note || "");
              setEditingNote(true);
            }}
            className="w-full text-left bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 hover:bg-amber-100 transition-colors flex items-start gap-2"
            data-testid={`cart-line-${line.item.id}-note-display`}
          >
            <i className="fas fa-pen-to-square text-amber-700 text-[10px] mt-0.5" />
            <span className="flex-1 text-[11px] font-medium text-amber-900 italic">
              "{line.note}"
            </span>
          </button>
        ) : (
          <button
            onClick={() => {
              setDraftNote("");
              setEditingNote(true);
            }}
            className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-brand-blue inline-flex items-center gap-1.5"
            data-testid={`cart-line-${line.item.id}-note-add`}
          >
            <i className="fas fa-plus" /> Add note
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between font-bold text-slate-600">
      <span>{label}</span>
      <span data-testid={testId}>{value}</span>
    </div>
  );
}
